import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { updateUserProfile, updateUserPassword, updateUserEmail, initializeUserProfile } from '../services/firebase';
import { getLevel } from '../utils/levelSystem';
import './Profile.css';

function Profile() {
    const { currentUser, userData, refreshUserData } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [dataLoadTimeout, setDataLoadTimeout] = useState(false);

    // Settings form states
    const [displayName, setDisplayName] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');

    // Feedback states
    const [profileMessage, setProfileMessage] = useState({ text: '', type: '' });
    const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });
    const [emailMessage, setEmailMessage] = useState({ text: '', type: '' });
    const [loading, setLoading] = useState(false);

    // Set timeout to show error if data doesn't load
    React.useEffect(() => {
        if (!userData && currentUser) {
            const timer = setTimeout(() => {
                setDataLoadTimeout(true);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [userData, currentUser]);

    const handleCreateProfile = async () => {
        setLoading(true);
        const result = await initializeUserProfile();
        if (result.success) {
            // Refresh the page to reload user data
            await refreshUserData();
            window.location.reload();
        } else {
            alert('Error creating profile: ' + result.error);
        }
        setLoading(false);
    };

    if (!currentUser) {
        navigate('/auth');
        return null;
    }

    if (!userData) {
        if (dataLoadTimeout) {
            return (
                <div className="profile-loading">
                    <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        background: 'rgba(220, 38, 38, 0.1)',
                        borderRadius: '12px',
                        border: '2px solid rgba(220, 38, 38, 0.3)',
                        maxWidth: '600px',
                        margin: '2rem auto'
                    }}>
                        <h2 style={{ color: '#ff6b6b', marginBottom: '1rem' }}>⚠️ Profile Not Found</h2>
                        <p style={{ marginBottom: '1rem' }}>Your profile doesn't exist in the database yet.</p>
                        <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>Click below to create it automatically:</p>
                        <button
                            onClick={handleCreateProfile}
                            disabled={loading}
                            style={{
                                padding: '1rem 2rem',
                                background: loading ? '#666' : '#667eea',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                marginBottom: '1rem'
                            }}
                        >
                            {loading ? '⏳ Creating Profile...' : '✨ Create My Profile'}
                        </button>
                        <p style={{ fontSize: '0.9rem', color: '#999' }}>
                            This will initialize your profile with starting stats (Level 1, 0 XP)
                        </p>
                    </div>
                </div>
            );
        }
        return (
            <div className="profile-loading">
                <div className="spinner"></div>
                <p>Loading profile...</p>
            </div>
        );
    }

    // Use centralized level system
    const levelInfo = getLevel(userData?.totalXP || 0);
    const totalXP = userData?.totalXP || 0;

    const userInitial = (currentUser.displayName || userData.displayName || 'L')[0].toUpperCase();

    const stats = {
        xp: totalXP,
        level: levelInfo.level,
        streak: userData.streak || 0,
        completedModules: userData.completedModules?.length || 0,
        totalQuestions: userData.stats?.totalQuestions || 0,
        correctAnswers: userData.stats?.correctAnswers || 0,
        studyTime: userData.stats?.studyTime || 0,
        lessonsCompleted: userData.stats?.lessonsCompleted || 0
    };

    const accuracy = stats.totalQuestions > 0
        ? Math.round((stats.correctAnswers / stats.totalQuestions) * 100)
        : 0;

    const studyTimeHours = Math.floor(stats.studyTime / 3600);
    const studyTimeMinutes = Math.floor((stats.studyTime % 3600) / 60);

    // Handler functions
    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        if (!displayName.trim()) {
            setProfileMessage({ text: 'Please enter a display name', type: 'error' });
            return;
        }

        setLoading(true);
        setProfileMessage({ text: '', type: '' });

        const result = await updateUserProfile(displayName.trim());

        if (result.success) {
            setProfileMessage({ text: 'Profile updated successfully!', type: 'success' });
            setDisplayName('');
            await refreshUserData();
            setTimeout(() => setProfileMessage({ text: '', type: '' }), 3000);
        } else {
            setProfileMessage({ text: result.error, type: 'error' });
        }

        setLoading(false);
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setPasswordMessage({ text: '', type: '' });

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMessage({ text: 'Please fill in all password fields', type: 'error' });
            return;
        }

        if (newPassword.length < 6) {
            setPasswordMessage({ text: 'New password must be at least 6 characters', type: 'error' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMessage({ text: 'New passwords do not match', type: 'error' });
            return;
        }

        setLoading(true);

        const result = await updateUserPassword(currentPassword, newPassword);

        if (result.success) {
            setPasswordMessage({ text: 'Password updated successfully!', type: 'success' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPasswordMessage({ text: '', type: '' }), 3000);
        } else {
            setPasswordMessage({ text: result.error, type: 'error' });
        }

        setLoading(false);
    };

    const handleUpdateEmail = async (e) => {
        e.preventDefault();
        setEmailMessage({ text: '', type: '' });

        if (!newEmail || !emailPassword) {
            setEmailMessage({ text: 'Please fill in all fields', type: 'error' });
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
            setEmailMessage({ text: 'Please enter a valid email address', type: 'error' });
            return;
        }

        setLoading(true);

        const result = await updateUserEmail(newEmail, emailPassword);

        if (result.success) {
            setEmailMessage({ text: 'Email updated successfully!', type: 'success' });
            setNewEmail('');
            setEmailPassword('');
            await refreshUserData();
            setTimeout(() => setEmailMessage({ text: '', type: '' }), 3000);
        } else {
            setEmailMessage({ text: result.error, type: 'error' });
        }

        setLoading(false);
    };

    return (
        <div className="profile-container">
            {/* Header Section */}
            <div className="profile-header">
                <div className="profile-banner">
                    <div className="banner-gradient"></div>
                </div>
                <div className="profile-info-section">
                    <div className="profile-avatar-large">
                        {userInitial}
                    </div>
                    <div className="profile-details">
                        <h1 className="profile-name">
                            {currentUser.displayName || userData.displayName || 'Learner'}
                        </h1>
                        <p className="profile-email">{currentUser.email}</p>
                        <div className="profile-level-badge">
                            <span className="badge-icon">{levelInfo.icon}</span>
                            <span className="badge-text">Level {levelInfo.level} - {levelInfo.title}</span>
                        </div>
                    </div>
                    <button
                        className="btn-edit-profile"
                        onClick={() => navigate('/dashboard')}
                    >
                        <span>⚙️</span>
                        <span>Settings</span>
                    </button>
                </div>
            </div>

            {/* Level Progress */}
            <div className="level-progress-section">
                <div className="level-info">
                    <div className="level-current">
                        <span className="level-icon">{levelInfo.icon}</span>
                        <span className="level-text">Level {levelInfo.level}</span>
                    </div>
                    <div className="level-xp">
                        {totalXP.toLocaleString()} XP
                    </div>
                    {!levelInfo.isMaxLevel && (
                        <div className="level-next">
                            <span className="level-icon">🎯</span>
                            <span className="level-text">Level {levelInfo.level + 1}</span>
                        </div>
                    )}
                </div>
                <div className="level-progress-bar">
                    <div
                        className="level-progress-fill"
                        style={{ width: `${levelInfo.progress}%` }}
                    >
                        <span className="progress-percent">{Math.round(levelInfo.progress)}%</span>
                    </div>
                </div>
                <p className="level-next-message">
                    {levelInfo.isMaxLevel
                        ? '🏆 Max level reached!'
                        : `${levelInfo.nextLevelXP - totalXP} XP to next level!`}
                </p>
            </div>

            {/* Tabs */}
            <div className="profile-tabs">
                <button
                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    📊 Overview
                </button>
                <button
                    className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stats')}
                >
                    📈 Statistics
                </button>
                <button
                    className={`tab-button ${activeTab === 'achievements' ? 'active' : ''}`}
                    onClick={() => setActiveTab('achievements')}
                >
                    🏆 Achievements
                </button>
                <button
                    className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    ⚙️ Settings
                </button>
            </div>

            {/* Tab Content */}
            <div className="profile-content">
                {activeTab === 'overview' && (
                    <div className="tab-panel">
                        <div className="stats-grid-profile">
                            <div className="stat-card-profile">
                                <div className="stat-icon-profile">💎</div>
                                <div className="stat-value-profile">{stats.xp.toLocaleString()}</div>
                                <div className="stat-label-profile">Total XP</div>
                            </div>
                            <div className="stat-card-profile">
                                <div className="stat-icon-profile">🔥</div>
                                <div className="stat-value-profile">{stats.streak}</div>
                                <div className="stat-label-profile">Day Streak</div>
                            </div>
                            <div className="stat-card-profile">
                                <div className="stat-icon-profile">✅</div>
                                <div className="stat-value-profile">{stats.completedModules}</div>
                                <div className="stat-label-profile">Modules Complete</div>
                            </div>
                            <div className="stat-card-profile">
                                <div className="stat-icon-profile">📚</div>
                                <div className="stat-value-profile">{stats.lessonsCompleted}</div>
                                <div className="stat-label-profile">Lessons Finished</div>
                            </div>
                        </div>

                        <div className="info-cards-grid">
                            <div className="info-card">
                                <h3>🎯 Accuracy</h3>
                                <div className="info-card-content">
                                    <div className="accuracy-circle">
                                        <svg viewBox="0 0 100 100">
                                            <circle cx="50" cy="50" r="45" className="circle-bg" />
                                            <circle
                                                cx="50"
                                                cy="50"
                                                r="45"
                                                className="circle-progress"
                                                style={{
                                                    strokeDashoffset: 283 - (283 * accuracy) / 100
                                                }}
                                            />
                                        </svg>
                                        <div className="accuracy-text">{accuracy}%</div>
                                    </div>
                                    <p className="accuracy-label">
                                        {stats.correctAnswers} / {stats.totalQuestions} correct
                                    </p>
                                </div>
                            </div>

                            <div className="info-card">
                                <h3>⏱️ Study Time</h3>
                                <div className="info-card-content">
                                    <div className="time-display">
                                        <div className="time-value">{studyTimeHours}h {studyTimeMinutes}m</div>
                                        <div className="time-label">Total time invested</div>
                                    </div>
                                    <div className="time-breakdown">
                                        <div className="time-item">
                                            <span className="time-icon">📖</span>
                                            <span>Lessons: {stats.lessonsCompleted}</span>
                                        </div>
                                        <div className="time-item">
                                            <span className="time-icon">❓</span>
                                            <span>Questions: {stats.totalQuestions}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="quick-actions-grid">
                            <button
                                className="action-card"
                                onClick={() => navigate('/dashboard')}
                            >
                                <span className="action-icon">📊</span>
                                <span className="action-text">Go to Dashboard</span>
                                <span className="action-arrow">→</span>
                            </button>
                            <button
                                className="action-card"
                                onClick={() => navigate('/analytics')}
                            >
                                <span className="action-icon">📈</span>
                                <span className="action-text">View Analytics</span>
                                <span className="action-arrow">→</span>
                            </button>
                            <button
                                className="action-card"
                                onClick={() => navigate('/flashcards')}
                            >
                                <span className="action-icon">📇</span>
                                <span className="action-text">Study Flashcards</span>
                                <span className="action-arrow">→</span>
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'stats' && (
                    <div className="tab-panel">
                        <h2 className="section-heading">Detailed Statistics</h2>
                        <div className="detailed-stats-grid">
                            <div className="detailed-stat">
                                <div className="detailed-stat-label">Total XP Earned</div>
                                <div className="detailed-stat-value">{stats.xp.toLocaleString()}</div>
                                <div className="detailed-stat-bar">
                                    <div
                                        className="detailed-stat-fill xp"
                                        style={{ width: `${Math.min((stats.xp / 1000) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                            <div className="detailed-stat">
                                <div className="detailed-stat-label">Current Streak</div>
                                <div className="detailed-stat-value">{stats.streak} days</div>
                                <div className="detailed-stat-bar">
                                    <div
                                        className="detailed-stat-fill streak"
                                        style={{ width: `${Math.min((stats.streak / 30) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                            <div className="detailed-stat">
                                <div className="detailed-stat-label">Modules Completed</div>
                                <div className="detailed-stat-value">{stats.completedModules}</div>
                                <div className="detailed-stat-bar">
                                    <div
                                        className="detailed-stat-fill modules"
                                        style={{ width: `${Math.min((stats.completedModules / 20) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                            <div className="detailed-stat">
                                <div className="detailed-stat-label">Accuracy Rate</div>
                                <div className="detailed-stat-value">{accuracy}%</div>
                                <div className="detailed-stat-bar">
                                    <div
                                        className="detailed-stat-fill accuracy"
                                        style={{ width: `${accuracy}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'achievements' && (
                    <div className="tab-panel">
                        <h2 className="section-heading">Your Achievements</h2>
                        <div className="achievements-grid-profile">
                            <div className={`achievement-badge-profile ${stats.xp >= 100 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">🎯</div>
                                <div className="achievement-name">First Steps</div>
                                <div className="achievement-desc">Earn 100 XP</div>
                            </div>
                            <div className={`achievement-badge-profile ${stats.xp >= 500 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">⭐</div>
                                <div className="achievement-name">Rising Star</div>
                                <div className="achievement-desc">Earn 500 XP</div>
                            </div>
                            <div className={`achievement-badge-profile ${stats.xp >= 1000 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">💎</div>
                                <div className="achievement-name">Knowledge Master</div>
                                <div className="achievement-desc">Earn 1000 XP</div>
                            </div>
                            <div className={`achievement-badge-profile ${stats.streak >= 7 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">🔥</div>
                                <div className="achievement-name">Week Warrior</div>
                                <div className="achievement-desc">7-day streak</div>
                            </div>
                            <div className={`achievement-badge-profile ${stats.streak >= 30 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">👑</div>
                                <div className="achievement-name">Month Master</div>
                                <div className="achievement-desc">30-day streak</div>
                            </div>
                            <div className={`achievement-badge-profile ${stats.completedModules >= 10 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">📚</div>
                                <div className="achievement-name">Module Expert</div>
                                <div className="achievement-desc">Complete 10 modules</div>
                            </div>
                            <div className={`achievement-badge-profile ${accuracy >= 90 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">🎓</div>
                                <div className="achievement-name">Perfectionist</div>
                                <div className="achievement-desc">90% accuracy</div>
                            </div>
                            <div className={`achievement-badge-profile ${stats.lessonsCompleted >= 20 ? 'unlocked' : 'locked'}`}>
                                <div className="achievement-icon-profile">🚀</div>
                                <div className="achievement-name">Unstoppable</div>
                                <div className="achievement-desc">Complete 20 lessons</div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="tab-panel">
                        <h2 className="section-heading">Account Settings</h2>

                        <div className="settings-section">
                            {/* Update Display Name */}
                            <div className="settings-card">
                                <h3 className="settings-card-title">👤 Update Display Name</h3>
                                <form onSubmit={handleUpdateProfile} className="settings-form">
                                    <div className="form-group">
                                        <label htmlFor="displayName">New Display Name</label>
                                        <input
                                            type="text"
                                            id="displayName"
                                            className="form-input"
                                            placeholder={currentUser.displayName || userData?.displayName || 'Enter new name'}
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                        />
                                    </div>
                                    {profileMessage.text && (
                                        <div className={`message ${profileMessage.type}`}>
                                            {profileMessage.text}
                                        </div>
                                    )}
                                    <button type="submit" className="btn-settings" disabled={loading}>
                                        {loading ? 'Updating...' : 'Update Name'}
                                    </button>
                                </form>
                            </div>

                            {/* Update Password */}
                            <div className="settings-card">
                                <h3 className="settings-card-title">🔒 Change Password</h3>
                                <form onSubmit={handleUpdatePassword} className="settings-form">
                                    <div className="form-group">
                                        <label htmlFor="currentPassword">Current Password</label>
                                        <input
                                            type="password"
                                            id="currentPassword"
                                            className="form-input"
                                            placeholder="Enter current password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="newPassword">New Password</label>
                                        <input
                                            type="password"
                                            id="newPassword"
                                            className="form-input"
                                            placeholder="Enter new password (min 6 characters)"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="confirmPassword">Confirm New Password</label>
                                        <input
                                            type="password"
                                            id="confirmPassword"
                                            className="form-input"
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </div>
                                    {passwordMessage.text && (
                                        <div className={`message ${passwordMessage.type}`}>
                                            {passwordMessage.text}
                                        </div>
                                    )}
                                    <button type="submit" className="btn-settings" disabled={loading}>
                                        {loading ? 'Updating...' : 'Change Password'}
                                    </button>
                                </form>
                            </div>

                            {/* Update Email */}
                            <div className="settings-card">
                                <h3 className="settings-card-title">📧 Change Email</h3>
                                <p className="settings-card-description">
                                    Current email: <strong>{currentUser.email}</strong>
                                </p>
                                <form onSubmit={handleUpdateEmail} className="settings-form">
                                    <div className="form-group">
                                        <label htmlFor="newEmail">New Email Address</label>
                                        <input
                                            type="email"
                                            id="newEmail"
                                            className="form-input"
                                            placeholder="Enter new email address"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="emailPassword">Current Password</label>
                                        <input
                                            type="password"
                                            id="emailPassword"
                                            className="form-input"
                                            placeholder="Confirm with your password"
                                            value={emailPassword}
                                            onChange={(e) => setEmailPassword(e.target.value)}
                                        />
                                    </div>
                                    {emailMessage.text && (
                                        <div className={`message ${emailMessage.type}`}>
                                            {emailMessage.text}
                                        </div>
                                    )}
                                    <button type="submit" className="btn-settings" disabled={loading}>
                                        {loading ? 'Updating...' : 'Change Email'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Profile;
